'use client';

import { c as _c } from "react/compiler-runtime";
import { useTranslations } from 'next-intl';
import { DataCell } from '@/components/data-cell';
import { UserAvatar } from '@/components/images/user-avatar';
import { type PartnerSocialLink, UserIdentity } from '@/components/profile/user-identity';
import { Icon } from '@/components/ui/icon';
import { Award01Icon, DocumentValidationIcon } from '@/components/ui/icon/glyphs';
import { Panel, PanelContent } from '@/components/ui/panel';
import { Link } from '@/i18n/navigation';
import { useFormattedCurrency } from '@/lib/currency';
import { formatRelativeTime } from '@/lib/format-relative-time';
import { useUserProfilePrefetchIntent } from '@/lib/sdk/prefetch';
export interface ProposalCreatorCardProps {
  creator: {
    walletAddress: string;
    displayName: string | null;
    handle: string | null;
    avatarUrl: string | null;
    avatarNsfw: boolean;
    avatarPreferences?: {
      rasterise: boolean;
    } | null;
    connectedProviders: ReadonlyArray<{
      provider: string;
      username?: string | null | undefined;
      displayName?: string | null | undefined;
    }>;
  };
  /** Partner creator: handshake mark plus their most prominent social account. */
  partner?: boolean;
  partnerSocial?: PartnerSocialLink | null | undefined;
  /** ISO timestamp string for when the proposal was created. */
  createdAt: string;
  /** Top market value across this creator's proposals, USDC encoded as a D6 string. */
  topMarketUsdcD6: string;
  /** Total number of proposals created by this user. */
  proposalCount: number;
  className?: string;
}
export function ProposalCreatorCard(t0) {
  const $ = _c(70);
  const {
    creator,
    partner: t1,
    partnerSocial: t2,
    createdAt,
    topMarketUsdcD6,
    proposalCount,
    className
  } = t0;
  const partner = t1 === undefined ? false : t1;
  const partnerSocial = t2 === undefined ? null : t2;
  const t = useTranslations("launchpad.creatorCard");
  const tTimeIntl = useTranslations("time");
  let t3;
  if ($[0] !== tTimeIntl) {
    t3 = (key, params) => tTimeIntl(key as Parameters<typeof tTimeIntl>[0], params);
    $[0] = tTimeIntl;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const tTime = t3;
  const {
    formatD6
  } = useFormattedCurrency();
  const creatorIntent = useUserProfilePrefetchIntent(creator.walletAddress);
  const t4 = `/portfolio/${creator.walletAddress}`;
  const t5 = creator.avatarPreferences?.rasterise ?? false;
  let t6;
  if ($[2] !== creator.avatarNsfw || $[3] !== creator.avatarUrl || $[4] !== creator.displayName || $[5] !== creator.walletAddress || $[6] !== t5) {
    t6 = <UserAvatar size="md" userId={creator.walletAddress} displayName={creator.displayName} avatarUrl={creator.avatarUrl} nsfw={creator.avatarNsfw} rasterise={t5} />;
    $[2] = creator.avatarNsfw;
    $[3] = creator.avatarUrl;
    $[4] = creator.displayName;
    $[5] = creator.walletAddress;
    $[6] = t5;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== t) {
    t7 = t("createdBy");
    $[8] = t;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== t7) {
    t8 = <span className="min-w-0 shrink truncate text-caption text-foreground-tertiary">{t7}</span>;
    $[10] = t7;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== createdAt || $[13] !== tTime) {
    t9 = formatRelativeTime(createdAt, tTime);
    $[12] = createdAt;
    $[13] = tTime;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== t9) {
    t10 = <span className="shrink-0 whitespace-nowrap text-caption text-foreground-tertiary">{t9}</span>;
    $[15] = t9;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] !== t10 || $[18] !== t8) {
    t11 = <div className="flex min-w-0 items-baseline justify-between gap-2">{t8}{t10}</div>;
    $[17] = t10;
    $[18] = t8;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] !== creator.avatarNsfw || $[21] !== creator.avatarUrl || $[22] !== creator.connectedProviders || $[23] !== creator.displayName || $[24] !== creator.handle || $[25] !== creator.walletAddress) {
    t12 = {
      userId: null,
      walletAddress: creator.walletAddress,
      displayName: creator.displayName,
      handle: creator.handle,
      avatarUrl: creator.avatarUrl,
      avatarNsfw: creator.avatarNsfw,
      connectedProviders: creator.connectedProviders
    };
    $[20] = creator.avatarNsfw;
    $[21] = creator.avatarUrl;
    $[22] = creator.connectedProviders;
    $[23] = creator.displayName;
    $[24] = creator.handle;
    $[25] = creator.walletAddress;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] !== creator.walletAddress) {
    t13 = creator.walletAddress.slice(0, 4);
    $[27] = creator.walletAddress;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  let t14;
  if ($[29] !== creator.walletAddress) {
    t14 = creator.walletAddress.slice(-4);
    $[29] = creator.walletAddress;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  const t15 = `${t13}…${t14}`;
  let t16;
  if ($[31] !== partner || $[32] !== partnerSocial || $[33] !== t12 || $[34] !== t15) {
    t16 = <span className="inline-flex min-w-0"><UserIdentity user={t12} variant="inline" size="md" partner={partner} partnerSocial={partnerSocial} fallback={t15} /></span>;
    $[31] = partner;
    $[32] = partnerSocial;
    $[33] = t12;
    $[34] = t15;
    $[35] = t16;
  } else {
    t16 = $[35];
  }
  let t17;
  if ($[36] !== t11 || $[37] !== t16) {
    t17 = <div className="flex min-w-0 flex-1 flex-col gap-y-0 leading-tight">{t11}{t16}</div>;
    $[36] = t11;
    $[37] = t16;
    $[38] = t17;
  } else {
    t17 = $[38];
  }
  let t18;
  if ($[39] !== creatorIntent || $[40] !== t17 || $[41] !== t4 || $[42] !== t6) {
    t18 = <Link href={t4} className="flex items-start gap-3" {...creatorIntent}>{t6}{t17}</Link>;
    $[39] = creatorIntent;
    $[40] = t17;
    $[41] = t4;
    $[42] = t6;
    $[43] = t18;
  } else {
    t18 = $[43];
  }
  let t19;
  if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <Icon icon={Award01Icon} />;
    $[44] = t19;
  } else {
    t19 = $[44];
  }
  let t20;
  if ($[45] !== t) {
    t20 = t("topMarket");
    $[45] = t;
    $[46] = t20;
  } else {
    t20 = $[46];
  }
  let t21;
  if ($[47] !== formatD6 || $[48] !== topMarketUsdcD6) {
    t21 = formatD6(topMarketUsdcD6, "compact");
    $[47] = formatD6;
    $[48] = topMarketUsdcD6;
    $[49] = t21;
  } else {
    t21 = $[49];
  }
  let t22;
  if ($[50] !== t20 || $[51] !== t21) {
    t22 = <DataCell icon={t19} label={t20} value={t21} cornerRadius="panelInset" className="flex-1" />;
    $[50] = t20;
    $[51] = t21;
    $[52] = t22;
  } else {
    t22 = $[52];
  }
  let t23;
  if ($[53] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <Icon icon={DocumentValidationIcon} />;
    $[53] = t23;
  } else {
    t23 = $[53];
  }
  let t24;
  if ($[54] !== t) {
    t24 = t("proposalsCreated");
    $[54] = t;
    $[55] = t24;
  } else {
    t24 = $[55];
  }
  let t25;
  if ($[56] !== proposalCount) {
    t25 = proposalCount.toString();
    $[56] = proposalCount;
    $[57] = t25;
  } else {
    t25 = $[57];
  }
  let t26;
  if ($[58] !== t24 || $[59] !== t25) {
    t26 = <DataCell icon={t23} label={t24} value={t25} cornerRadius="panelInset" className="flex-1" />;
    $[58] = t24;
    $[59] = t25;
    $[60] = t26;
  } else {
    t26 = $[60];
  }
  let t27;
  if ($[61] !== t22 || $[62] !== t26) {
    t27 = <div className="flex gap-3 sm:gap-4">{t22}{t26}</div>;
    $[61] = t22;
    $[62] = t26;
    $[63] = t27;
  } else {
    t27 = $[63];
  }
  let t28;
  if ($[64] !== t18 || $[65] !== t27) {
    t28 = <PanelContent className="flex flex-col gap-4">{t18}{t27}</PanelContent>;
    $[64] = t18;
    $[65] = t27;
    $[66] = t28;
  } else {
    t28 = $[66];
  }
  let t29;
  if ($[67] !== className || $[68] !== t28) {
    t29 = <Panel surface="solid" density="compact" className={className}>{t28}</Panel>;
    $[67] = className;
    $[68] = t28;
    $[69] = t29;
  } else {
    t29 = $[69];
  }
  return t29;
}